import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import axios from "axios";

import StepPhoto from "./StepPhoto";
import StepTranscription from "./StepTranscription";
import StepConfirm from "./StepConfirm";

import { setAlert } from "../../redux/actions/alert";

function WizardModal({ isAuthenticated, resetProducts, setAlert }) {
  const [step, setStep] = useState(1);
  const [images, setImages] = useState([null, null, null, null]);

  const initialProductData = {
    name: "",
    price: "",
    compare_price: "",
    description: "",
    category: "",
    type: "",
    condition: "",
    size: "",
    color: "",
    material: "",
    brand: "",
    quantity: "",
    active: true,
    // Se han removido los campos de ubicación
  };
  const [productData, setProductData] = useState(initialProductData);

  const [hasCameraPermission, setHasCameraPermission] = useState(null);

  const wasAuthenticatedRef = useRef(isAuthenticated);

  const requestCameraPermission = () => {
    navigator.mediaDevices
      .getUserMedia({ video: true })
      .then((stream) => {
        stream.getTracks().forEach((track) => track.stop());
        setHasCameraPermission(true);
      })
      .catch((err) => {
        console.warn("Camera permission not granted or error:", err);
        setHasCameraPermission(false);
      });
  };

  useEffect(() => {
    const storedImages = localStorage.getItem("wizardImages");
    if (storedImages) setImages(JSON.parse(storedImages));

    const storedProductData = localStorage.getItem("wizardProductData");
    if (storedProductData) setProductData(JSON.parse(storedProductData));

    const storedStep = localStorage.getItem("wizardStep");
    if (storedStep) setStep(JSON.parse(storedStep));

    requestCameraPermission();
  }, [setAlert]);

  useEffect(() => {
    if (!wasAuthenticatedRef.current && isAuthenticated) {
      const storedImages = localStorage.getItem("wizardImages");
      if (storedImages) setImages(JSON.parse(storedImages));
      const storedProductData = localStorage.getItem("wizardProductData");
      if (storedProductData) setProductData(JSON.parse(storedProductData));
      const storedStep = localStorage.getItem("wizardStep");
      if (storedStep) setStep(JSON.parse(storedStep));

      localStorage.removeItem("wizardImages");
      localStorage.removeItem("wizardProductData");
      localStorage.removeItem("wizardStep");
    }
    wasAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      localStorage.setItem("wizardImages", JSON.stringify(images));
      localStorage.setItem("wizardProductData", JSON.stringify(productData));
      localStorage.setItem("wizardStep", JSON.stringify(step));
    }
  }, [images, productData, step, isAuthenticated]);

  const nextStep = () => setStep((prev) => prev + 1);
  const prevStep = () => setStep((prev) => prev - 1);

  async function convertMediaToFile(media, idx) {
    if (media.startsWith("data:")) {
      return dataURLtoFile(media, `image${idx + 1}.jpg`);
    } else if (media.startsWith("blob:")) {
      const res = await fetch(media);
      const blob = await res.blob();
      return new File([blob], `video${idx + 1}.webm`, { type: blob.type });
    }
    return null;
  }

  const dataURLtoFile = (dataUrl, filename) => {
    if (!dataUrl || !dataUrl.includes(",")) {
      console.error("Invalid dataUrl format:", dataUrl);
      return null;
    }
    const arr = dataUrl.split(",");
    const match = arr[0].match(/:(.*?);/);
    if (!match) {
      console.error("Could not get MIME from dataUrl:", dataUrl);
      return null;
    }
    const mime = match[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  };

  const handleSubmitProduct = async () => {
    if (!hasCameraPermission) {
      setAlert(
        "Cannot upload products without camera permission.",
        "warning"
      );
      return;
    }

    const config = {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `JWT ${localStorage.getItem("access")}`,
      },
    };
    const formDataToSubmit = new FormData();

    formDataToSubmit.append("name", productData.name || "");
    formDataToSubmit.append("price", productData.price || "");
    formDataToSubmit.append("description", productData.description || "");
    formDataToSubmit.append("type", productData.type || "");
    formDataToSubmit.append("condition", productData.condition || "");
    formDataToSubmit.append("size", productData.size || "");
    formDataToSubmit.append("color", productData.color || "");
    formDataToSubmit.append("material", productData.material || "");
    formDataToSubmit.append("brand", productData.brand || "");
    formDataToSubmit.append("quantity", productData.quantity || "");
    formDataToSubmit.append(
      "active",
      productData.active !== undefined ? productData.active : true
    );
    formDataToSubmit.append("year", productData.year || "");
    formDataToSubmit.append("bibliography", JSON.stringify(productData.bibliography || []));


    const files = await Promise.all(
      images.map((img, idx) =>
        img ? convertMediaToFile(img, idx) : Promise.resolve(null)
      )
    );
    files.forEach((file) => {
      if (file) {
        formDataToSubmit.append("images", file);
      }
    });

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/product/product-burble/`,
        formDataToSubmit,
        config
      );
      if (response.status === 201) {
        setAlert("Product successfully created and uploaded.", "success");
        localStorage.removeItem("wizardImages");
        localStorage.removeItem("wizardProductData");
        localStorage.removeItem("wizardStep");

        resetProducts();
        setProductData(initialProductData);
        setImages([null, null, null, null]);
        setStep(1);
        document.getElementById("my_modal_5").close();
      }
    } catch (err) {
      console.error(err);
      if (err.response && err.response.data) {
        const errorData = err.response.data;
        if (errorData.detail) {
          setAlert(errorData.detail, "warning");
        } else if (typeof errorData === "string") {
          setAlert(errorData, "warning");
        } else {
          setAlert("An error occurred while creating the product.", "warning");
        }
      } else {
        setAlert(
          "An unexpected error occurred. Please try again.",
          "warning"
        );
      }
    }
  };

  const reRequestPermissions = () => {
    requestCameraPermission();
  };

  if (hasCameraPermission === false) {
    return (
      <dialog id="my_modal_5" className="modal modal-bottom sm:modal-middle open">
        <div className="modal-box">
          <button
            onClick={() => document.getElementById("my_modal_5").close()}
            className="absolute top-2 right-2 text-gray-600 hover:text-black px-2 py-2"
          >
            X
          </button>
          <p className="mb-4">
            You have blocked the camera permission. To continue, please enable the camera permission in your browser settings.
          </p>
          <button
            onClick={reRequestPermissions}
            className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600"
          >
            Retry Requesting Permissions
          </button>
        </div>
      </dialog>
    );
  }


  const resetWizard = () => {
    setProductData(initialProductData);
    setImages([null, null, null, null]);
    setStep(1);
    localStorage.removeItem("wizardImages");
    localStorage.removeItem("wizardProductData");
    localStorage.removeItem("wizardStep");
  };
  return (
    <dialog id="my_modal_5" className="modal modal-bottom sm:modal-middle open">
      <div className="modal-box">
        <button
          onClick={() => document.getElementById("my_modal_5").close()}
          className="absolute top-2 right-2 text-gray-600 hover:text-black px-2 py-2"
        >
          X
        </button>

        {step === 1 && (
          <StepPhoto
            images={images}
            setImages={setImages}
            nextStep={nextStep}
          />
        )}
        {step === 2 && (
          <StepTranscription
            productData={productData}
            setProductData={setProductData}
            prevStep={prevStep}
            nextStep={nextStep}
            isAuthenticated={isAuthenticated}
            handleCancel={resetWizard}
          />
        )}
        {step === 3 && (
          <StepConfirm
            images={images}
            productData={productData}
            prevStep={prevStep}
            handleSubmitProduct={handleSubmitProduct}
            isAuthenticated={isAuthenticated}
            handleCancel={resetWizard}
          />
        )}
      </div>
    </dialog>
  );
}

const mapStateToProps = (state) => ({
  isAuthenticated: state.Auth.isAuthenticated,
});

export default connect(mapStateToProps, { setAlert })(WizardModal);
